import React, { useState, useEffect, useMemo } from "react";
import { MdClose } from "react-icons/md";
import { FaRegCheckSquare, FaRegPlusSquare } from "react-icons/fa";
import { GoSearch } from "react-icons/go";

import { CircularProgress } from "@mui/material";
import tw from "tailwind-styled-components";
import { ScrollableContent } from "../Styles/InputStyles";
import axios from "axios";
import { useQuery } from "react-query";
import Config from "../../Config";
import { useSelector } from "react-redux";

const LinkUnlink = ({ linkPropertyMutate, selectedData, setLinkProperty, linkProperty }) => {
    const leadId = selectedData?.id
    const [searchText, setSearchText] = useState("");
    const user = useSelector((state) => state.UserReducer.user);
    const fetchListing = async () =>
        await axios.get(`${Config.apiUrl}/lead/linked/properties/${leadId}`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
            },
        });

    const { isLoading, error, data, refetch, isRefetching } = useQuery(
        ['propertyData', leadId],
        fetchListing,
        {
            enabled: Boolean(linkProperty && leadId),
        }
    );

    const property = data?.data?.data
    const [linkedProperties, setLinkedProperties] = useState([]);
    const [isApiInProgress, setIsApiInProgress] = useState(false);

    useEffect(() => {
        if (property && Array.isArray(property)) {
            setLinkedProperties(property)
        }
    }, [property]);

    const filteredProperties = useMemo(() => {
        if (!searchText.trim()) return linkedProperties;

        const searchLower = searchText.toLowerCase();
        return linkedProperties.filter(prop =>
            prop.name?.toLowerCase().includes(searchLower) ||
            prop.address?.toLowerCase().includes(searchLower) ||
            prop.city?.toLowerCase().includes(searchLower)
        );
    }, [linkedProperties, searchText]);

    const handleToggle = (propId, isLinked) => {
        if (isApiInProgress) return;

        setIsApiInProgress(true);
        setLinkedProperties((prev) =>
            prev.map((prop) =>
                prop.id === propId ? { ...prop, isLoading: true } : prop
            )
        );

        linkPropertyMutate(
            {
                leadId: selectedData?.id,
                toggleValue: isLinked ? 0 : 1,
                propId: propId,
            },
            {
                onSuccess: () => {
                    setLinkedProperties((prev) =>
                        prev.map((prop) =>
                            prop.id === propId
                                ? { ...prop, isLinked: !isLinked, isLoading: false }
                                : prop
                        )
                    );
                    setIsApiInProgress(false);
                },
                onError: () => {
                    setLinkedProperties((prev) =>
                        prev.map((prop) =>
                            prop.id === propId ? { ...prop, isLoading: false } : prop
                        )
                    );
                    setIsApiInProgress(false);
                },
            }
        );
    };

    return (
        <Container>
            <Header>
                <h1 className="text-white text-lg font-medium">Manage Linked Properties</h1>
                <button onClick={() => setLinkProperty(false)}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            <div className="px-4 pt-4 relative">
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search property by name"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="w-full px-8 h-12 py-2 border border-gray-200 rounded-md focus:outline-none focus:border-primary-color text-gray-800 placeholder-gray-300"
                    />
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                        <GoSearch className="w-4 h-4 text-gray-400" />
                    </div>
                    {searchText && (
                        <button
                            onClick={() => setSearchText("")}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            <MdClose size={18} />
                        </button>
                    )}
                </div>
            </div>

            {(isLoading || isRefetching || (!property && linkProperty)) && (
                <div className="flex justify-center items-center my-40">
                    <CircularProgress size={40} />
                </div>)}
            {!isLoading && !isRefetching && linkedProperties.length > 0 && (
                <ScrollableContent className="max-h-screen overflow-y-auto px-6 py-4">
                    {filteredProperties.length === 0 ? (
                        <div className="text-center py-8">
                            <div className="text-gray-400 mb-2">
                                <GoSearch size={32} className="mx-auto" />
                            </div>
                            <p className="text-gray-500">No properties found matching "{searchText}"</p>
                            <button
                                onClick={() => setSearchText("")}
                                className="mt-2 text-primary-color hover:underline text-sm"
                            >
                                Clear search
                            </button>
                        </div>
                    ) : (
                        <>
                            {filteredProperties.map((prop) => (
                                <PropertyRow key={prop.id}>
                                    <Label>{prop.name}</Label>
                                    <button
                                        onClick={() => handleToggle(prop.id, prop.isLinked)}
                                        disabled={prop.isLoading || isApiInProgress}
                                    >
                                        {prop.isLoading ? (
                                            <CircularProgress size={20} />
                                        ) : prop.isLinked ? (
                                            <FaRegCheckSquare size={20} className="text-green-500 font-normal text-sm" />
                                        ) : (
                                            <FaRegPlusSquare size={20} className="text-gray-400 font-normal text-sm" />
                                        )}
                                    </button>
                                </PropertyRow>
                            ))}
                        </>
                    )}
                </ScrollableContent>)}
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const PropertyRow = tw.div`flex items-center justify-between py-5 border-b`;
const Label = tw.span`text-gray-600`;

export default LinkUnlink;
